import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useAuthorityLevels } from '@/hooks/useAuthorityLevel';
import { Error404 } from '@/components/custom/error';
import { FilterSelect } from '@/components/custom/filter/filter-select';
import { downloadReportFile } from '@/utils/downloadReportFile';
import { formatInteger, formatPercentage } from '../LiveChallenge/formatters';
import { DailyChart, HourlyChart, RankingChart } from './TiaChatCharts';
import { MessagesTable } from './MessagesTable';
import { SectionState } from './SectionState';
import { useReportSection } from './useReportSection';
import { SAMPLE_OPTIONS, readFilters, toQuery } from './filters';
import { INTENT_LABELS, OUTCOME_LABELS, PROMPT_KIND_LABELS, label } from './labels';

const BASE_PATH = '/reports/tia_chat_conversations';
const DATE_INPUT_CLASSES =
  'w-0 min-w-0 flex-1 border-0 bg-transparent p-0 text-inherit [font:inherit] [color-scheme:light] [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:mr-1 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:p-0';

const ROLE_OPTIONS = [
  { value: 'all', label: 'Aluno e T.I.A' },
  { value: 'student', label: 'Só aluno' },
  { value: 'tia', label: 'Só T.I.A' }
];

const toOptions = labels => Object.entries(labels).map(([value, text]) => ({ value, label: text }));

function Field({ id, title, children }) {
  if (children.type === Select) return React.cloneElement(children, { label: title });

  return (
    <div className="min-w-0 [&_:is(input,select)]:h-10 [&_:is(input,select)]:w-full [&_:is(input,select)]:min-w-0 [&_:is(input,select)]:rounded-full [&_:is(input,select)]:border [&_:is(input,select)]:border-solid [&_:is(input,select)]:border-neutral-lightest [&_:is(input,select)]:bg-bright [&_:is(input,select)]:text-xs [&_:is(input,select)]:font-normal [&_:is(input,select)]:text-neutral-dark [&_[data-slot=combobox-input]]:pl-4">
      <label
        htmlFor={id}
        className="mb-1 block text-xs text-neutral-dark"
      >
        {title}
      </label>
      {React.cloneElement(children, { 'aria-label': title })}
    </div>
  );
}

function Select({ value, onChange, options, placeholder = 'Todos', label, searchable = false }) {
  return (
    <FilterSelect
      label={label}
      options={options.map(option => ({ id: option.value, name: option.label }))}
      value={value}
      onValueChange={onChange}
      blankLabel={placeholder ?? false}
      searchable={searchable}
      wrapperClassName="min-w-0 md:w-full [&_label]:mb-0 [&_label]:text-xs [&_label]:font-normal [&_label]:text-neutral-dark"
      className="rounded-full border-neutral-lightest bg-bright pl-4 text-xs font-normal text-neutral-dark"
    />
  );
}

// Relatório de uso do chat da T.I.A (somente Administrador). Sem link nem card: acesso direto pela URL.
export function TiaChatReport() {
  const client = useClient();
  const isAdmin = useAuthorityLevels().includes('admin');
  const [searchParams, setSearchParams] = useSearchParams();
  const applied = useMemo(() => readFilters(searchParams), [searchParams]);
  const [draft, setDraft] = useState(applied);
  const [institutions, setInstitutions] = useState([]);
  const [classrooms, setClassrooms] = useState([]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  useEffect(() => setDraft(applied), [applied]);

  useEffect(() => {
    if (!isAdmin) return;
    client.fetch('/institutions?simple=true').then(({ data, error }) => {
      if (error) return toast.error('Erro ao buscar as escolas.');
      setInstitutions(data.map(item => ({ value: String(item.id), label: item.name })));
    });
    client.fetch('/classrooms?simple=true').then(({ data, error }) => {
      if (error) return toast.error('Erro ao buscar as turmas.');
      setClassrooms(data.map(item => ({ value: String(item.id), label: item.title, institution: String(item['institution-id']) })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const query = useMemo(() => toQuery(applied), [applied]);
  const queryString = new URLSearchParams(query).toString();

  const summary = useReportSection(() => (isAdmin ? client.fetch(`${BASE_PATH}/summary?${queryString}`) : Promise.resolve({})), [queryString, isAdmin]);
  const messages = useReportSection(() => (isAdmin ? client.fetch(`${BASE_PATH}?${queryString}`) : Promise.resolve({})), [queryString, isAdmin]);

  const kpis = summary.data?.kpis;
  const charts = summary.data?.charts;
  const classroomOptions = classrooms.filter(item => !draft.institution_id || item.institution === draft.institution_id);
  const dynamicOptions = rows => (rows || []).filter(row => row.key !== 'n/d').map(row => ({ value: row.key, label: label({ ...INTENT_LABELS, ...OUTCOME_LABELS }, row.key) }));

  const update = (key, value) => setDraft(current => ({ ...current, [key]: value || undefined }));

  const apply = event => {
    event?.preventDefault();
    setSearchParams(new URLSearchParams(Object.entries(draft).filter(([, value]) => value)), { replace: true });
  };

  const clear = () => setSearchParams(new URLSearchParams(), { replace: true });

  const handleExport = async () => {
    try {
      await downloadReportFile(`${BASE_PATH}.xlsx`, query);
    } catch (error) {
      toast.error('Erro ao baixar o relatório');
    }
  };

  if (!isAdmin) return <Error404 />;

  const cards = [
    { id: 'total', title: 'Mensagens dos alunos', value: formatInteger(kpis?.total_messages), variation: 'report-kpi-card--lightning' },
    { id: 'students', title: 'Alunos que conversaram', value: formatInteger(kpis?.unique_students), variation: 'report-kpi-card--users' },
    { id: 'per-student', title: 'Mensagens por aluno', value: kpis ? String(kpis.messages_per_student).replace('.', ',') : '-', variation: 'report-kpi-card--teachers' },
    { id: 'flagged', title: 'Sinalizadas', value: kpis ? `${formatInteger(kpis.flagged)} (${formatPercentage(kpis.flagged_pct)})` : '-', variation: 'report-kpi-card--lightning' },
    { id: 'without-ai', title: 'Respondidas sem IA', value: kpis ? `${formatInteger(kpis.without_ai)} (${formatPercentage(kpis.without_ai_pct)})` : '-', variation: 'report-kpi-card--users' },
    { id: 'errors', title: 'Erros', value: formatInteger(kpis?.errors), variation: 'report-kpi-card--teachers' },
    { id: 'latency', title: 'Tempo médio de resposta', value: kpis?.avg_total_ms ? `${(kpis.avg_total_ms / 1000).toFixed(1).replace('.', ',')} s` : '-', variation: 'report-kpi-card--lightning' },
    { id: 'tokens', title: 'Tokens (JEV / IA entrada / IA saída)', value: kpis ? [kpis.jev_tokens, kpis.ai_input_tokens, kpis.ai_output_tokens].map(formatInteger).join(' / ') : '-', variation: 'report-kpi-card--users' }
  ];

  const widgets = [
    { id: 'daily', title: 'Mensagens por dia', subtitle: 'Total e sinalizadas no período', node: <DailyChart data={charts?.by_day} /> },
    { id: 'hourly', title: 'Mensagens por hora do dia', node: <HourlyChart data={charts?.by_hour} /> },
    {
      id: 'outcome',
      title: 'Como as mensagens terminaram',
      node: (
        <RankingChart
          rows={charts?.by_outcome}
          labelFor={key => label(OUTCOME_LABELS, key)}
        />
      )
    },
    { id: 'section', title: 'Seções da plataforma citadas', node: <RankingChart rows={charts?.by_section} /> },
    {
      id: 'intent',
      title: 'Intenção das mensagens',
      node: (
        <RankingChart
          rows={charts?.by_intent}
          labelFor={key => label(INTENT_LABELS, key)}
        />
      )
    },
    {
      id: 'prompt',
      title: 'Prompt usado pela IA',
      node: (
        <RankingChart
          rows={charts?.by_prompt_kind}
          labelFor={key => label(PROMPT_KIND_LABELS, key)}
        />
      )
    },
    { id: 'institution', title: 'Escolas com mais mensagens', subtitle: 'As 10 primeiras', node: <RankingChart rows={charts?.by_institution} /> }
  ];

  return (
    <div className="report">
      <header className="report__header">
        <h1 className="report__title">Chat da T.I.A</h1>
        <p className="report__description">Uso do chat pelos alunos, mensagens sinalizadas e caminho percorrido por cada resposta</p>
      </header>

      <main className="report__body">
        <form
          className="relative grid grid-cols-1 items-end gap-3 rounded-md border border-solid border-neutral-lighter p-4 min-[480px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 [&_:is(input,select):focus-visible]:outline [&_:is(input,select):focus-visible]:outline-2 [&_:is(input,select):focus-visible]:outline-offset-2 [&_:is(input,select):focus-visible]:outline-primary"
          onSubmit={apply}
        >
          <div
            className="relative col-span-full flex h-10 w-full min-w-0 items-center gap-2 rounded-full border border-solid border-neutral-lightest bg-bright px-2 text-[13px] font-light text-neutral-dark md:col-span-1"
            role="group"
            aria-label="Período"
          >
            <label
              className="sr-only"
              htmlFor="tia-from"
            >
              Data de início
            </label>
            <input
              id="tia-from"
              className={DATE_INPUT_CLASSES}
              type="date"
              value={draft.from}
              onChange={event => update('from', event.target.value)}
            />
            <span
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 h-4 w-px -translate-x-px -translate-y-2 overflow-hidden border-l border-solid border-neutral-lighter text-transparent"
            >
              |
            </span>
            <label
              className="sr-only"
              htmlFor="tia-to"
            >
              Data de fim
            </label>
            <input
              id="tia-to"
              className={DATE_INPUT_CLASSES}
              type="date"
              value={draft.to}
              min={draft.from}
              onChange={event => update('to', event.target.value)}
            />
          </div>
          <Field title="Escola">
            <Select
              options={institutions}
              value={draft.institution_id ?? ''}
              onChange={value => setDraft(current => ({ ...current, institution_id: value || undefined, classroom_id: undefined }))}
              placeholder="Todas"
              searchable
            />
          </Field>
          <Field title="Turma">
            <Select
              options={classroomOptions}
              value={draft.classroom_id ?? ''}
              onChange={value => update('classroom_id', value)}
              placeholder="Todas"
              searchable
            />
          </Field>
          <Field
            id="tia-role"
            title="Quem enviou"
          >
            <Select
              id="tia-role"
              value={draft.role || 'all'}
              onChange={value => update('role', value)}
              options={ROLE_OPTIONS}
              placeholder={null}
            />
          </Field>
          <Field
            id="tia-outcome"
            title="Desfecho"
          >
            <Select
              id="tia-outcome"
              value={draft.outcome}
              onChange={value => update('outcome', value)}
              options={toOptions(OUTCOME_LABELS)}
            />
          </Field>
          <Field
            id="tia-intent"
            title="Intenção"
          >
            <Select
              id="tia-intent"
              value={draft.intent}
              onChange={value => update('intent', value)}
              options={toOptions(INTENT_LABELS)}
            />
          </Field>
          <Field
            id="tia-section"
            title="Seção"
          >
            <Select
              id="tia-section"
              value={draft.section}
              onChange={value => update('section', value)}
              options={dynamicOptions(charts?.by_section)}
            />
          </Field>
          <Field
            id="tia-prompt"
            title="Prompt da IA"
          >
            <Select
              id="tia-prompt"
              value={draft.prompt_kind}
              onChange={value => update('prompt_kind', value)}
              options={toOptions(PROMPT_KIND_LABELS)}
            />
          </Field>
          <Field
            id="tia-path"
            title="Tela (trecho da URL)"
          >
            <input
              type="search"
              id="tia-path"
              className="report-form__control"
              value={draft.path || ''}
              onChange={event => update('path', event.target.value)}
              placeholder="Ex: /aulas-ao-vivo"
            />
          </Field>
          <Field
            id="tia-student"
            title="Aluno (e-mail ou id)"
          >
            <input
              type="search"
              id="tia-student"
              className="report-form__control"
              value={draft.student || ''}
              onChange={event => update('student', event.target.value.trim())}
              placeholder="Ex: aluno@escola.com"
            />
          </Field>
          <Field
            id="tia-sample"
            title="Amostra (conversas)"
          >
            <Select
              id="tia-sample"
              value={draft.sample}
              onChange={value => update('sample', value)}
              options={SAMPLE_OPTIONS.map(size => ({ value: String(size), label: String(size) }))}
              placeholder={null}
            />
          </Field>
          <label className="m-0 flex min-h-10 cursor-pointer items-center gap-3 text-sm text-neutral-dark">
            <input
              type="checkbox"
              className="m-0 h-4 w-4 flex-[0_0_16px] accent-primary"
              checked={draft.flagged === 'true'}
              onChange={event => update('flagged', event.target.checked ? 'true' : undefined)}
            />
            Só sinalizadas
          </label>
          <div className="col-span-full flex items-center gap-3 md:col-span-1 [&_.btn]:min-h-10 [&_.btn]:rounded-full [&_.btn]:px-4 [&_.btn]:py-2">
            <button
              type="button"
              className="btn btn--outline"
              onClick={clear}
            >
              Limpar
            </button>
            <button
              type="submit"
              className="btn btn--primary"
            >
              Aplicar
            </button>
          </div>
        </form>

        <div className="report-widget">
          <div className="report-widget__header">
            <h3 className="report-widget__title">Visão geral</h3>
          </div>
          <div className="report-widget__body">
            <SectionState
              loading={summary.loading}
              error={summary.error}
              onRetry={summary.reload}
              errorTitle="Não foi possível carregar os indicadores"
            >
              <div className="report-kpi-card-wrapper">
                {cards.map(card => (
                  <div
                    className={`report-kpi-card ${card.variation}`}
                    key={card.id}
                  >
                    <h3 className="report-kpi-card__title">{card.title}</h3>
                    <p className="report-kpi-card__value">{card.value}</p>
                  </div>
                ))}
              </div>
            </SectionState>
          </div>
        </div>

        {widgets.map(widget => (
          <div
            className="report-widget"
            key={widget.id}
          >
            <div className="report-widget__header">
              <div className="report-widget__inner">
                <h3 className="report-widget__title">{widget.title}</h3>
                {widget.subtitle && <p className="report-widget__subtitle">{widget.subtitle}</p>}
              </div>
            </div>
            <div className="report-widget__body">
              <SectionState
                loading={summary.loading}
                error={summary.error}
                onRetry={summary.reload}
                errorTitle="Não foi possível carregar o gráfico"
              >
                {widget.node}
              </SectionState>
            </div>
          </div>
        ))}

        <div className="report-widget">
          <div className="report-widget__header">
            <div className="report-widget__inner">
              <h3 className="report-widget__title">Conversas por amostragem</h3>
              <p className="report-widget__subtitle">{messages.meta ? `${formatInteger(messages.meta.sample_size)} de ${formatInteger(messages.meta.total_matching)} mensagens do filtro (semente ${messages.meta.seed}).` : ''}</p>
            </div>
            <button
              type="button"
              className="btn btn--outline"
              onClick={handleExport}
            >
              Exportar XLSX
            </button>
          </div>
          <div className="report-widget__body">
            <SectionState
              loading={messages.loading}
              error={messages.error}
              onRetry={messages.reload}
              errorTitle="Não foi possível carregar as conversas"
            >
              <MessagesTable items={messages.data?.items} />
            </SectionState>
          </div>
        </div>
      </main>
    </div>
  );
}
